import { motion } from "framer-motion";
import { ExternalLink, Github, ArrowUpRight } from "lucide-react";
import { useTheme } from "../hooks/useTheme";

const ProjectCard = ({ project, onClick, index }) => {
  const { isDarkMode } = useTheme();

  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.08, ease: "easeOut" }}
      onClick={() => onClick(project)}
      className={`group relative cursor-pointer rounded-2xl overflow-hidden border transition-all duration-300 ${
        isDarkMode
          ? "bg-graphite-darkest/70 border-graphite-medium/20 hover:border-graphite-silver/40"
          : "bg-white/80 border-gray-200 hover:border-gray-400"
      } backdrop-blur-sm hover:shadow-2xl hover:shadow-graphite-void/60 hover:-translate-y-1`}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${project.title}`}
      onKeyDown={(e) => e.key === "Enter" && onClick(project)}
    >
      {/* Image / gradient thumbnail */}
      <div className="relative h-56 overflow-hidden py-4">
        {/* Gradient always renders as the background */}
        <div
          className={`absolute inset-0 bg-gradient-to-br ${project.gradient} transition-transform duration-500 group-hover:scale-105`}
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: project.lightGradient
                ? "repeating-linear-gradient(127deg, transparent 0px, rgba(0,0,0,0.04) 0.5px, rgba(0,0,0,0.08) 1px, transparent 2px, transparent 8px)"
                : "repeating-linear-gradient(127deg, transparent 0px, rgba(255,255,255,0.04) 0.5px, rgba(255,255,255,0.08) 1px, transparent 2px, transparent 8px)",
            }}
          />
        </div>

        {/* Logo / featured image — contained with padding so it never overflows */}
        {project.featuredImage && (
          <img
            src={project.featuredImage}
            alt={project.title}
            className="absolute inset-0 w-full h-full object-contain p-8 transition-transform duration-500 group-hover:scale-105 drop-shadow-lg"
          />
        )}

        {/* Category badge — solid dark so it reads on any image colour */}
        <span className="absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider bg-graphite-void/75 text-white backdrop-blur-sm border border-white/10">
          {project.category}
        </span>

        {/* Open icon hint on hover */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div
            className={`p-1.5 rounded-full backdrop-blur-sm ${
              isDarkMode
                ? "bg-graphite-darkest/70 text-white"
                : "bg-white/80 text-graphite-darkest"
            }`}
          >
            <ArrowUpRight size={14} />
          </div>
        </div>

        {/* Shimmer line at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-graphite-silver/30 to-transparent" />
      </div>

      {/* Card body */}
      <div className="p-5">
        <h3
          className={`text-lg font-bold mb-2 transition-colors duration-200 ${
            isDarkMode
              ? "text-graphite-crystal group-hover:text-white"
              : "text-graphite-darkest group-hover:text-graphite-steel"
          }`}
        >
          {project.title}
        </h3>

        <p
          className={`text-sm leading-relaxed mb-4 line-clamp-2 ${
            isDarkMode ? "text-graphite-silver" : "text-graphite-medium"
          }`}
        >
          {project.shortDescription}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {project.tags?.slice(0, 4).map((tag) => (
            <span
              key={tag}
              className={`text-xs px-2 py-0.5 rounded-md font-medium ${
                isDarkMode
                  ? "bg-graphite-dark/80 text-graphite-platinum border border-graphite-medium/30"
                  : "bg-gray-100 text-graphite-medium border border-gray-200"
              }`}
            >
              {tag}
            </span>
          ))}
          {project.tags?.length > 4 && (
            <span
              className={`text-xs px-2 py-0.5 rounded-md ${
                isDarkMode ? "text-graphite-light" : "text-graphite-slate"
              }`}
            >
              +{project.tags.length - 4}
            </span>
          )}
        </div>

        {/* Quick links */}
        <div className="flex gap-3 pt-3 border-t border-graphite-medium/20">
          {project.links.live && (
            <a
              href={project.links.live}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={`flex items-center gap-1 text-xs font-medium transition-colors duration-200 ${
                isDarkMode
                  ? "text-graphite-silver hover:text-graphite-shine"
                  : "text-graphite-medium hover:text-graphite-steel"
              }`}
              aria-label={`Live site for ${project.title}`}
            >
              <ExternalLink size={12} />
              Live
            </a>
          )}
          {project.links.github && (
            <a
              href={project.links.github}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={`flex items-center gap-1 text-xs font-medium transition-colors duration-200 ${
                isDarkMode
                  ? "text-graphite-silver hover:text-graphite-shine"
                  : "text-graphite-medium hover:text-graphite-steel"
              }`}
              aria-label={`GitHub repo for ${project.title}`}
            >
              <Github size={12} />
              GitHub
            </a>
          )}
          <span
            className={`ml-auto text-xs font-medium ${
              isDarkMode ? "text-graphite-light" : "text-graphite-slate"
            }`}
          >
            View details →
          </span>
        </div>
      </div>
    </motion.article>
  );
};

export default ProjectCard;
